<template>
  <div
    v-if="activeBanners.length > 0"
    ref="barRef"
    :class="[alertClass, 'mb-0 rounded-0 py-0']"
    role="region"
    aria-label="상단 공지 배너"
    style="z-index: 1000; transition: background-color 0.3s ease"
  >
    <div
      class="container d-flex align-items-center justify-content-between gap-3 h-100"
      style="min-height: 48px"
    >
      <div class="flex-grow-1" style="height: 48px; overflow: hidden">
        <Swiper
          :modules="modules"
          direction="vertical"
          :autoplay="{
            delay: 3500,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }"
          :loop="activeBanners.length > 1"
          :allow-touch-move="false"
          @slideChange="onSlideChange"
          style="height: 100%"
        >
          <SwiperSlide
            v-for="b in activeBanners"
            :key="b.bannerId"
            class="d-flex align-items-center"
          >
            <div class="d-flex align-items-center gap-2 text-truncate w-100">
              <span class="fw-bold flex-shrink-0">{{
                b.title || "[공지]"
              }}</span>
              <span class="text-truncate" style="white-space: pre-line">
                {{ b.message }}
              </span>
              <button
                v-if="b.href?.trim()"
                type="button"
                class="btn btn-link p-0 ms-2 text-decoration-underline text-reset flex-shrink-0"
                @click="goHref(b.href)"
              >
                {{ b.ctaText || "자세히 보기" }}
              </button>
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      <div
        class="d-flex align-items-center gap-1 flex-shrink-0 opacity-100 border-100 rounded"
        style="background: rgba(255, 255, 255, 0.8); padding: 2px 6px"
      >
        <span
          class="text-dark small"
          style="cursor: pointer; font-size: 0.85rem"
          @click="hideCurrentBanner"
        >
          오늘 하루 보지 않기
        </span>
        <button
          type="button"
          class="btn-close btn-close-dark"
          style="font-size: 0.65rem"
          aria-label="닫기"
          @click="hideCurrentBanner"
        ></button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  watch,
} from "vue";
import { useRouter } from "vue-router";

import { logWarn, logError } from "@/utils/logHelper";
import siteTopBannerPublicApi from "@/common/api/siteTopBannerPublicApi";

// Swiper 관련 Import
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay } from "swiper/modules";
import "swiper/css";

const router = useRouter();
const modules = [Autoplay];

const barRef = ref(null);
const activeBanners = ref([]); // 실제로 화면에 띄울 배너 배열
const currentIndex = ref(0); // 현재 보여지고 있는 Swiper 인덱스

// 로컬 스토리지 키 생성 (버전 업그레이드 시 새로 보여주기 위해 versionNo 필수)
function buildStorageKey(bannerId, versionNo) {
  return `mpnite_top_banner_hide_${bannerId}_v${versionNo || 1}`;
}

function endOfTodayTs() {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d.getTime();
}

function readHideUntil(bannerId, versionNo) {
  if (!bannerId) return 0;
  const raw = localStorage.getItem(buildStorageKey(bannerId, versionNo));
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

// Swiper 슬라이드가 변경될 때마다 인덱스 추적
function onSlideChange(swiper) {
  // loop 모드일 때는 realIndex를 사용해야 정확한 원본 데이터 배열 인덱스를 잡습니다.
  currentIndex.value = swiper.realIndex;
}

// 배경색 동적 변경: 현재 보고 있는 배너의 테마(variant)를 전체 띠배너 배경으로 적용
const alertClass = computed(() => {
  if (activeBanners.value.length === 0) return "alert alert-dark";
  // 인덱스가 배열 크기를 초과하지 않도록 안전 장치
  const safeIndex = currentIndex.value % activeBanners.value.length;
  const v = activeBanners.value[safeIndex]?.variant || "dark";
  return `alert alert-${v}`;
});

function setCssVarHeight(px) {
  document.documentElement.style.setProperty("--app-top-notice-h", `${px}px`);
}

function applyOffsets() {
  const h =
    activeBanners.value.length > 0 ? barRef.value?.offsetHeight || 0 : 0;
  setCssVarHeight(h);
}

function onResize() {
  applyOffsets();
}

// "오늘 하루 보지 않기" 클릭 시
async function hideCurrentBanner() {
  const safeIndex = currentIndex.value % activeBanners.value.length;
  const current = activeBanners.value[safeIndex];

  if (!current?.bannerId) return;

  // 1. 현재 슬라이드 로컬 스토리지에 숨김 처리 저장
  const until = endOfTodayTs();
  localStorage.setItem(
    buildStorageKey(current.bannerId, current.versionNo),
    String(until),
  );

  // 2. 화면에 띄워진 배열에서 해당 배너 제거
  activeBanners.value = activeBanners.value.filter(
    (b) => b.bannerId !== current.bannerId,
  );
  currentIndex.value = 0; // 인덱스 초기화

  // 3. UI 높이 재조정
  await nextTick();
  applyOffsets();
}

function goHref(href) {
  if (!href) return;
  if (href.startsWith("/")) {
    router.push(href);
  } else {
    window.open(href, "_blank", "noopener,noreferrer");
  }
}

async function fetchActiveBanner() {
  try {
    const data = await siteTopBannerPublicApi.fetchActive();

    const list = Array.isArray(data) ? data : data ? [data] : [];

    const now = Date.now();
    activeBanners.value = list.filter((b) => {
      const hideUntil = readHideUntil(b.bannerId, b.versionNo);
      return hideUntil <= now;
    });

    await nextTick();
    applyOffsets();
  } catch (e) {
    // 배너는 필수 기능이 아니므로 실패 시 조용히 무시
    logWarn("[TopNoticeBanner] 배너 로드 실패:", e?.message);
    activeBanners.value = [];
  }
}

onMounted(async () => {
  await fetchActiveBanner();
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
});

watch(
  () => activeBanners.value.length,
  async () => {
    await nextTick();
    applyOffsets();
  },
);
</script>

<style scoped>
.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Swiper Slide 수직 중앙 정렬 보정 */
.swiper-slide {
  height: 48px; /* 컨테이너 높이와 동일하게 맞춤 */
}
</style>
